import { KpiOrgTargetInput } from '../../../../_generated/graphql'
import {
  KpiDashboardItem,
  KpiMonthlyOrgTarget,
  KpiMonthlyOrgTargets,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'

export interface OrgTargetField {
  itemId: string
  label: string
  targetRate: number | null
  hasInvalidInput: boolean
}

export interface OrgTargetRow {
  source: KpiMonthlyOrgTarget
  periodLabel: string
  values: string[]
  isCleared: boolean
}

/** 初期表示の年月をローカル日時から取得する。 */
export function getCurrentOrgTargetPeriod(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

/** 対象月以前の最新行を、配列の並び順に依存せず取得する。解除行も引き継ぐ。 */
export function findEffectiveOrgTarget(
  targets: KpiMonthlyOrgTargets,
  period: string,
): KpiMonthlyOrgTarget | null {
  return targets.reduce<KpiMonthlyOrgTarget | null>((effective, target) => {
    if (
      target.period <= period &&
      (effective == null || target.period > effective.period)
    ) {
      return target
    }
    return effective
  }, null)
}

/** 選択月に適用される行から、編集・引き継ぎ・未設定の状態を説明する。 */
export function describeOrgTargetFormState(
  period: string,
  effective: KpiMonthlyOrgTarget | null,
): string {
  if (effective == null) return 'この月に適用されている組織目標はありません'
  const formatPeriod = (value: string): string =>
    `${value.slice(0, 4)}年${value.slice(5)}月`
  if (effective.period === period) {
    return `${formatPeriod(period)}の設定を編集しています`
  }
  if (effective.targets.length === 0) {
    return `${formatPeriod(effective.period)}に解除されているため、現在は未設定です`
  }
  return `${formatPeriod(effective.period)}の設定を引き継いでいます。保存すると ${formatPeriod(period)}以降はこの内容で上書きされます`
}

/** 対象月と目標率を検証する。空欄と 0 は未設定、100 超は有効。 */
export function validateOrgTargetForm(
  period: string,
  fields: readonly OrgTargetField[],
): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) {
    return '対象年月を YYYY-MM 形式で入力してください。'
  }
  if (
    fields.some(
      ({ targetRate, hasInvalidInput }) =>
        hasInvalidInput ||
        (targetRate != null &&
          (!Number.isFinite(targetRate) || targetRate < 0)),
    )
  ) {
    return '目標率は 0 以上の数値で入力してください。'
  }
  return ''
}

/** 有効な正数の目標だけを送信用に変換する。不正値は送信しない。 */
export function buildOrgTargetInputs(
  fields: readonly OrgTargetField[],
): KpiOrgTargetInput[] {
  return fields.flatMap(({ itemId, targetRate, hasInvalidInput }) => {
    if (hasInvalidInput) {
      throw new Error(`Invalid organization target input: ${itemId}`)
    }
    if (targetRate == null || targetRate === 0) return []
    if (!Number.isFinite(targetRate) || targetRate < 0) {
      throw new Error(`Invalid organization target rate: ${itemId}`)
    }
    return [{ itemId, targetRate }]
  })
}

/** 定義順でフォームを作る。適用行がない場合や行に含まれない項目は未設定。 */
export function buildOrgTargetFields(
  items: readonly KpiDashboardItem[],
  saved: KpiMonthlyOrgTarget | null,
): OrgTargetField[] {
  return items.map((item) => {
    const target =
      saved == null
        ? undefined
        : saved.targets.find((value) => value.itemId === item.id)
    return {
      itemId: item.id,
      label: item.kpiName,
      targetRate: target == null ? null : target.targetRate,
      hasInvalidInput: false,
    }
  })
}

/** 設定履歴を API の年月順、KPI の定義順で表示する。 */
export function buildOrgTargetRows(
  items: readonly KpiDashboardItem[],
  targets: KpiMonthlyOrgTargets,
): OrgTargetRow[] {
  return targets.map((source) => ({
    source,
    periodLabel: `${source.period.slice(0, 4)}年${source.period.slice(5)}月`,
    isCleared: source.targets.length === 0,
    values: buildOrgTargetFields(items, source).map(({ targetRate }) =>
      targetRate == null || targetRate === 0 ? '—' : `${targetRate}%`,
    ),
  }))
}
